import * as THREE from 'three';

/** Soft gaussian stamp for sparks — one lobe, no stepped rings. */
export function createGlowTexture() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(size, size);
  const cx = (size - 1) * 0.5;
  const inv = 1 / cx;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = (x - cx) * inv;
      const dy = (y - cx) * inv;
      const d2 = dx * dx + dy * dy;
      const a = d2 >= 1 ? 0 : Math.exp(-d2 * 4.8);
      const i = (y * size + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = 255;
      img.data[i + 3] = Math.round(a * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  return tex;
}

function tagColor(node) {
  const tint = node?.clusterTint;
  if (typeof tint === 'string' && /^#[0-9a-fA-F]{6}$/.test(tint.trim())) {
    return new THREE.Color(tint.trim());
  }
  return new THREE.Color('#8a90c8');
}

/** Disk colour is the primary tag. Completed is a touch brighter; open is a touch quieter. */
export function nodeColor(node) {
  const c = tagColor(node);
  if (node?.isExcluded) return c.multiplyScalar(0.32);
  if (node?.isLocked) return c.multiplyScalar(0.52);
  if (node?.statusCategory === 'completed') return c.offsetHSL(0, 0.035, 0.03);
  return c.offsetHSL(0, -0.045, -0.028);
}

export function nodeOpacity(node) {
  if (node.isExcluded) return 0.3;
  if (node.isLocked) return 0.62;
  if (node.statusCategory === 'completed') return 0.9;
  return 0.96;
}

export function nodeEmissiveIntensity(node) {
  if (!node) return 0.7;
  if (node.isExcluded) return 0.12;
  if (node.isLocked) return 0.28;
  if (node.isAvailable) return 1.15;
  if (node.statusCategory === 'in_progress') return 0.95;
  if (node.statusCategory === 'completed') return 0.28;
  if (node.statusCategory === 'blocked') return 0.7;
  return 0.55;
}

const OPEN_FILL = new THREE.Color('#14161b');

const NODE_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vRing;
  varying vec3 vFill;
  varying float vHollow;
  varying float vOpacity;
  uniform vec3 uFill;
  uniform vec3 uRing;
  uniform float uHollow;
  uniform float uOpacity;
  void main() {
    vUv = uv;
    vRing = uRing;
    vFill = uFill;
    vHollow = uHollow;
    vOpacity = uOpacity;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const NODE_FRAG = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vRing;
  varying vec3 vFill;
  varying float vHollow;
  varying float vOpacity;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p);
    float aa = max(fwidth(r), 0.002);
    float disk = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, r);
    if (disk < 0.004) discard;
    float ringMask = smoothstep(0.72 - aa, 0.72 + aa, r);
    float grey = dot(vRing, vec3(0.30, 0.54, 0.16));
    vec3 ring = mix(vRing, vec3(grey), 0.12 * vHollow);
    vec3 col = mix(vFill, ring, ringMask * vHollow);
    // Open nodes keep an opaque plate so wires can run under the disk.
    float fillA = mix(vOpacity, min(1.0, vOpacity * 1.25), vHollow);
    gl_FragColor = vec4(col, disk * fillA);
  }
`;

function isOpenNode(node) {
  return node?.statusCategory !== 'completed';
}

/** Hollow open nodes keep a dark fill; completed disks stay solid tag colour. */
export function applyNodeStatusLook(mat, node) {
  if (!mat?.uniforms?.uHollow) return;
  const hollow = isOpenNode(node) ? 1 : 0;
  mat.uniforms.uHollow.value = hollow;
  if (hollow) mat.uniforms.uFill.value.copy(OPEN_FILL);
  else mat.uniforms.uFill.value.copy(mat.uniforms.uRing.value);
}

export function createNodeMaterial(node) {
  const ring = nodeColor(node);
  const hollow = isOpenNode(node) ? 1 : 0;
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uFill: { value: hollow ? OPEN_FILL.clone() : ring.clone() },
      uRing: { value: ring.clone() },
      uHollow: { value: hollow },
      uOpacity: { value: 0 },
    },
    vertexShader: NODE_VERT,
    fragmentShader: NODE_FRAG,
    transparent: true,
    depthWrite: true,
    fog: false,
    toneMapped: false,
  });
  Object.defineProperty(mat, 'color', {
    get() { return mat.uniforms.uRing.value; },
    set(v) {
      mat.uniforms.uRing.value.copy(v);
      if (mat.uniforms.uHollow.value < 0.5) mat.uniforms.uFill.value.copy(v);
    },
    configurable: true,
  });
  Object.defineProperty(mat, 'opacity', {
    get() { return mat.uniforms.uOpacity.value; },
    set(v) { mat.uniforms.uOpacity.value = v; },
    configurable: true,
  });
  return mat;
}

const HALO_PLANE = new THREE.PlaneGeometry(2, 2);
/** Shared live-glow radius in disk units — available and in-progress match. */
export const HALO_LIVE_BREATHE = 7.4;
const HALO_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vOpacity;
  varying float vPulse;
  varying float vElectrons;
  varying float vInner;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uPulse;
  uniform float uElectrons;
  uniform float uInner;
  void main() {
    vUv = uv;
    vColor = uColor;
    vOpacity = uOpacity;
    vPulse = uPulse;
    vElectrons = uElectrons;
    vInner = uInner;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
export const HALO_FRAG = /* glsl */ `
  uniform float uSharp;
  uniform float uTime;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vOpacity;
  varying float vPulse;
  varying float vElectrons;
  varying float vInner;

  float electron(vec2 p, float t, vec2 axis, float rx, float ry, float tilt) {
    float c = cos(t);
    float s = sin(t);
    float depth = s * tilt;
    float persp = 1.0 / max(1.0 - depth * 0.32, 0.55);
    vec2 q = vec2(c * rx, s * ry);
    vec2 o = vec2(q.x * axis.x - q.y * axis.y, q.x * axis.y + q.y * axis.x) * persp;
    float front = clamp(depth * 0.5 + 0.5, 0.0, 1.0);
    float d = length(p - o);
    float core = exp(-d * d * mix(1400.0, 880.0, front)) * mix(0.22, 0.52, front);
    float haze = exp(-d * d * 220.0) * mix(0.025, 0.07, front);
    return core + haze;
  }

  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = length(p);
    float k = mix(3.4, 5.2, uSharp) + vPulse * 1.2;
    float outer = exp(-r * r * k);
    outer *= 1.0 - smoothstep(0.58, 0.96, r);
    float inner = 0.0;
    if (vInner > 0.001) {
      inner = exp(-r * r * mix(14.0, 18.0, uSharp)) * vInner;
      inner *= 1.0 - smoothstep(0.28, 0.62, r);
    }
    float glow = outer * mix(1.0, 0.55, clamp(vInner, 0.0, 1.0)) + inner * 0.9;
    float sparks = 0.0;
    if (vElectrons > 0.001) {
      sparks += electron(p, uTime * 1.28, vec2(1.0, 0.0), 0.30, 0.16, 0.78) * vElectrons;
      sparks += electron(p, uTime * 1.02 + 2.05, vec2(0.18, 0.98), 0.27, 0.15, 0.70) * vElectrons;
    }
    float a = (glow + sparks) * vOpacity;
    if (a < 0.0006) discard;
    vec3 col = mix(vColor, vec3(1.0), clamp(sparks * 0.18, 0.0, 0.22));
    gl_FragColor = vec4(col, a);
  }
`;

/** Camera-facing wash behind available / in-progress disks. */
export function createNodeHalo(node) {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: nodeColor(node) },
      uOpacity: { value: 0 },
      uPulse: { value: 0 },
      uSharp: { value: 0.28 },
      uTime: { value: 0 },
      uElectrons: { value: node?.statusCategory === 'in_progress' ? 1 : 0 },
      uInner: { value: node?.isAvailable ? 1 : 0 },
    },
    vertexShader: HALO_VERT,
    fragmentShader: HALO_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    fog: false,
  });
  Object.defineProperty(mat, 'color', {
    get() { return mat.uniforms.uColor.value; },
    set(v) { mat.uniforms.uColor.value.copy(v); },
    configurable: true,
  });
  Object.defineProperty(mat, 'opacity', {
    get() { return mat.uniforms.uOpacity.value; },
    set(v) { mat.uniforms.uOpacity.value = v; },
    configurable: true,
  });
  const mesh = new THREE.Mesh(HALO_PLANE, mat);
  mesh.userData.sharedGeom = true;
  mesh.renderOrder = 1;
  mesh.frustumCulled = false;
  return mesh;
}

/**
 * Camera-facing selection ring. Drawn in a shader so the band stays circular
 * and anti-aliased when the camera is close — a low-poly torus reads as a
 * hexagon under bloom.
 */
export function createSelectionRing() {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(0xffffff) },
      uOpacity: { value: 0.9 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv * 2.0 - 1.0;
        float r = length(p);
        float mid = 0.40;
        float halfW = 0.0075;
        float aa = max(fwidth(r) * 1.2, 0.002);
        float band = abs(r - mid);
        float ring = 1.0 - smoothstep(halfW - aa, halfW + aa, band);
        if (ring < 0.004) discard;
        gl_FragColor = vec4(uColor, ring * uOpacity);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    toneMapped: false,
    fog: false,
  });
  Object.defineProperty(mat, 'color', {
    get() { return mat.uniforms.uColor.value; },
    set(v) { mat.uniforms.uColor.value.copy(v); },
    configurable: true,
  });
  Object.defineProperty(mat, 'opacity', {
    get() { return mat.uniforms.uOpacity.value; },
    set(v) { mat.uniforms.uOpacity.value = v; },
    configurable: true,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.visible = false;
  mesh.renderOrder = 1000;
  mesh.frustumCulled = false;
  return mesh;
}

/**
 * Paint-only hover outline. Own mesh — never scales the mission disk,
 * so nest-clear / wires / titles do not move.
 */
export function createHoverRing() {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(0xffffff) },
      uOpacity: { value: 0.95 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv * 2.0 - 1.0;
        float r = length(p);
        float mid = 0.78;
        float halfW = 0.032;
        float aa = max(fwidth(r) * 1.2, 0.002);
        float ring = 1.0 - smoothstep(halfW - aa, halfW + aa, abs(r - mid));
        float glow = smoothstep(mid - 0.05, mid, r) * (1.0 - smoothstep(mid, mid + 0.16, r));
        float a = max(ring, glow * 0.42) * uOpacity;
        if (a < 0.004) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    fog: false,
  });
  Object.defineProperty(mat, 'color', {
    get() { return mat.uniforms.uColor.value; },
    set(v) { mat.uniforms.uColor.value.copy(v); },
    configurable: true,
  });
  Object.defineProperty(mat, 'opacity', {
    get() { return mat.uniforms.uOpacity.value; },
    set(v) { mat.uniforms.uOpacity.value = v; },
    configurable: true,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.visible = false;
  mesh.renderOrder = 999;
  mesh.frustumCulled = false;
  mesh.userData.hoverRingMid = 0.78;
  return mesh;
}
